import { useCallback, useContext, useMemo, useState } from 'react';

import { MoreHoriz } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Button,
    IconButton,
    Stack,
    Table,
    TableCell,
    TableHead,
    TableRow,
    TableBody,
    Checkbox,
    styled,
    Typography
} from '@mui/material';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    flexRender,
    type ColumnDef,
    type RowSelectionState,
    type CellContext
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { useRecordingActions } from './useRecordingActions';
import { useRecordingDialogs } from './useRecordingDialogs';
import { getRecordingName, recordingDuration, recordingTimes } from './util';
import { ClassContext } from '../../context/ClassContext';
import { Roles } from '../../context/types';
import { Permission } from '../../types/recordingTypes';
import { NoRecords } from '../classroom/SharedComponents';
import { type ClassRecording } from '../classroom/types';
import { type ClassRecordingExtended } from '../classroom/ui-types';
import Loadable from '../common/loadable';
import { PositionableMenu } from '../common/positionableMenu';

const ActionsBox = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 4px;
`;

const TableCellTime = styled(TableCell)`
    width: 20%;
`;

const TableCellName = styled(TableCell)`
    width: 30%;
`;

const TableCellDuration = styled(TableCell)`
    width: 15%;
`;

const TableCellStatus = styled(TableCell)`
    width: 15%;
`;

const TableCellStudentAccess = styled(TableCell)`
    width: 10%;
`;

const TableCellAction = styled(TableCell)`
    width: auto;
`;

const TableCellNoWrap = styled(TableCell)`
    white-space: nowrap;
`;

const LoadableButtonWrapper = styled(Loadable)`
    &[aria-busy='true'] {
        .loading-overlay {
            align-items: start;
        }
        .MuiCircularProgress-root {
            width: 16px !important;
            height: 16px !important;
            margin-left: 8px;
        }
        button.MuiButtonBase-root {
            padding-left: 24px;
            padding-right: 8px;
        }
    }
`;

const BulkActionsContainer = styled(Box)`
    display: flex;
    gap: 20px;
    justify-content: end;
    align-items: end;
    margin: 16px 0;
`;

const TableRowNoBorder = styled(TableRow)`
    .MuiTableCell-root {
        border: none;
    }
`;

const RecordingMenuButton = styled(Button)`
    min-width: fit-content;
    padding: 2px 3px;
    margin-right: 2px;
`;

type RecordingsTableProps = {
    recordings: ClassRecordingExtended[];
    setRecordings: (recordings: ClassRecordingExtended[]) => void;
    isLoading: boolean;
    isFetching: boolean;
    refetch?: () => void;
    isGuest: boolean;
    hasAdminPrivileges?: boolean;
};

export const RecordingsTable = (props: RecordingsTableProps) => {
    const { recordings, setRecordings, isLoading, isFetching, isGuest, refetch, hasAdminPrivileges } = props;

    const { me, adminSettings, userUuid, classUuid } = useContext(ClassContext);

    const isInstructor = hasAdminPrivileges || [Roles.Teacher, Roles.Assistant].includes(me.role as Roles);
    const allowBulkOperations = isInstructor && adminSettings?.options.bulkRecordingOperations;

    const { t } = useTranslation();

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const { renderDialogs, dialogSetters } = useRecordingDialogs({
        recordings,
        setRecordings,
        classUuid,
        userUuid,
        isInstructor,
        rowSelection,
        setRowSelection
    });

    const { handleBulkChangePermissions, renderPopover, getActions, handleLaunch } = useRecordingActions({
        recordings,
        setRecordings,
        classUuid,
        userUuid,
        isInstructor,
        rowSelection,
        setRowSelection,
        dialogSetters
    });

    const [anchorPos, setAnchorPos] = useState<any>();
    const [menuRecording, setMenuRecording] = useState<ClassRecording | undefined>();

    const renderRecordingTimes = (recording: ClassRecording) => {
        const { date, times } = recordingTimes(recording);
        return (
            <>
                <Box>{date}</Box>
                <Box>{times}</Box>
            </>
        );
    };

    const renderRecordingDuration = (recording: ClassRecording) => {
        const duration = recordingDuration(recording);
        if (!duration) {
            return null;
        }
        const { hoursStr, minutesStr, secondsStr } = duration;
        return (
            <Box>
                {hoursStr}:{minutesStr}:{secondsStr}
            </Box>
        );
    };

    const renderPermission = useCallback(
        (row: ClassRecordingExtended) => {
            const { permission } = row;
            const permissionsString = t(`lti:recordings.permissions.${permission.toLowerCase()}`);

            return <Box>{permissionsString}</Box>;
        },
        [t]
    );

    const countSelectedRecordings = useMemo(
        () => Object.values(rowSelection).filter((s) => Boolean(s)).length,
        [rowSelection]
    );

    const selectColumns: ColumnDef<ClassRecordingExtended>[] = !allowBulkOperations
        ? []
        : [
              {
                  id: 'select',
                  header: ({ table }) => (
                      <TableCell key="select">
                          <Checkbox
                              indeterminate={table.getIsSomeRowsSelected()}
                              checked={table.getIsAllRowsSelected()}
                              onChange={table.getToggleAllRowsSelectedHandler()}
                          />
                      </TableCell>
                  ),
                  cell: ({ row }) => (
                      <TableCell key={`${row.id}_select`}>
                          <Checkbox
                              checked={row.getIsSelected()}
                              disabled={!row.getCanSelect()}
                              onChange={row.getToggleSelectedHandler()}
                          />
                      </TableCell>
                  )
              }
          ];

    const getStudentAccessStatus = (info: CellContext<ClassRecordingExtended, unknown>) => {
        const { permission, restrictions } = info.cell.row.original;
        const mode = restrictions?.mode;
        if (permission !== Permission.ROSTER) {
            return t('lti:recordings.student-access.status-na');
        }

        if (!mode) {
            return t(`lti:recordings.student-access.status-site`);
        }

        return t(`lti:recordings.student-access.status-${mode}`);
    };

    const recordingActions = useMemo(
        () => (menuRecording ? getActions(menuRecording) : []),
        [menuRecording, getActions]
    );

    const columns: ColumnDef<ClassRecordingExtended>[] = selectColumns.concat([
        {
            accessorKey: 'when',
            header: (def) => <TableCellTime key={def.column.id}>{t('class:time')}</TableCellTime>,
            cell: (info) => (
                <TableCellNoWrap key={info.cell.id}>{renderRecordingTimes(info.row.original)}</TableCellNoWrap>
            )
        },
        {
            accessorKey: 'name',
            header: (def) => <TableCellName key={def.column.id}>{t('class:name')}</TableCellName>,
            cell: (info) => <TableCell key={info.cell.id}>{getRecordingName(t, info.row.original)}</TableCell>
        },
        {
            accessorKey: 'duration',
            header: (def) => <TableCellDuration key={def.column.id}>{t('class:duration')}</TableCellDuration>,
            cell: (info) => (
                <TableCellNoWrap key={info.cell.id}>{renderRecordingDuration(info.row.original)}</TableCellNoWrap>
            )
        },
        {
            accessorKey: 'permission',
            header: (def) => <TableCellStatus key={def.column.id}>{t('class:whocanview')}</TableCellStatus>,
            cell: (info) => <TableCell key={info.cell.id}>{renderPermission(info.row.original)}</TableCell>
        },
        {
            accessorKey: 'restrictions',
            header: (def) => (
                <TableCellStudentAccess key={def.column.id}>
                    {t('lti:recordings.student-access.title')}
                </TableCellStudentAccess>
            ),
            cell: (info) => <TableCell key={info.cell.id}>{getStudentAccessStatus(info)}</TableCell>
        },
        {
            id: 'actions',
            header: (def) => <TableCellAction key={def.column.id}>{t('class:actions')}</TableCellAction>,
            cell: (info) => {
                const recording = info.row.original;
                return (
                    <TableCell key={info.cell.id} sx={{ position: 'relative', overflow: 'visible' }}>
                        <ActionsBox>
                            <RecordingMenuButton
                                variant="outlined"
                                aria-label="more"
                                id={`action-button-${menuRecording?.recordingId}`}
                                aria-controls={anchorPos ? `action-menu-${menuRecording?.recordingId}` : undefined}
                                aria-expanded={anchorPos ? true : undefined}
                                aria-haspopup="true"
                                onClick={(e) => {
                                    setAnchorPos(e.currentTarget.getBoundingClientRect());
                                    setMenuRecording(recording);
                                }}
                                title={t('lti:recordings.actions')}
                            >
                                <MoreHoriz color="primary" fontSize="large" />
                            </RecordingMenuButton>

                            {!isGuest && ['AVAILABLE', 'EXTERNAL'].includes(recording.status) ? (
                                <LoadableButtonWrapper isLoading={recording.isLoading}>
                                    <Button
                                        sx={{ minWidth: '140px' }}
                                        color="primary"
                                        variant="contained"
                                        onClick={() => handleLaunch(recording)}
                                    >
                                        {t('lti:recordings.launch')}
                                    </Button>
                                </LoadableButtonWrapper>
                            ) : (
                                <Box sx={{ width: '100%', textAlign: 'center', fontSize: '14px' }}>
                                    {t(`lti:recordings.statusShort.${recording.status.toLowerCase()}`)}
                                </Box>
                            )}
                        </ActionsBox>
                    </TableCell>
                );
            }
        }
    ]);

    const table = useReactTable({
        data: recordings,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onRowSelectionChange: setRowSelection, // Track selection state in this component.
        state: {
            rowSelection // Pass selection state from this component to tanstack table.
        },
        enableRowSelection: true,
        getRowId: (row) => row.recordingId
    });

    const renderTable = () => (
        <Table>
            <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) =>
                            flexRender(header.column.columnDef.header, header.getContext())
                        )}
                    </TableRow>
                ))}
            </TableHead>
            <TableBody>
                {table.getRowModel().rows.map((row) => (
                    <TableRowNoBorder key={row.id} selected={row.getIsSelected()}>
                        {row.getVisibleCells().map((cell) => flexRender(cell.column.columnDef.cell, cell.getContext()))}
                    </TableRowNoBorder>
                ))}
            </TableBody>
        </Table>
    );

    const handleClose = () => {
        setAnchorPos(null);
    };

    return (
        <Loadable fullScreen={false} isLoading={isLoading}>
            {renderDialogs()}
            <PositionableMenu
                analyticsId="recording-actions"
                actionId={menuRecording?.recordingId}
                anchorPos={anchorPos}
                actions={recordingActions}
                onClose={handleClose}
            />

            {allowBulkOperations && countSelectedRecordings > 0 && (
                <BulkActionsContainer>
                    <Typography>
                        {t('lti:recordings.bulk-actions.select-count', { count: countSelectedRecordings })}
                    </Typography>
                    <Button onClick={handleBulkChangePermissions} variant="contained">
                        {t('lti:recordings.bulk-actions.change-permissions')}
                    </Button>
                </BulkActionsContainer>
            )}

            {renderTable()}

            {renderPopover()}

            {recordings.length === 0 && (
                <NoRecords message={t('lti:recordings.noneformeeting')}>
                    {refetch && (
                        <IconButton onClick={() => refetch()} disabled={isFetching} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    )}
                </NoRecords>
            )}
        </Loadable>
    );
};
